import React from "react";
import { SavedDocument } from "../../models/document-storage.types";
import { DocumentRow } from "./DocumentRow";

/** Columns the list can be ordered by; each maps to a SavedDocument field. */
export type SortKey = "title" | "document_type" | "created_at";
export type SortDirection = "asc" | "desc";

export interface SortState {
  key: SortKey;
  direction: SortDirection;
}

const COLUMNS: { key: SortKey; label: string; modifier?: string }[] = [
  { key: "title", label: "Name", modifier: "document-list__head-cell--title" },
  { key: "document_type", label: "Type" },
  { key: "created_at", label: "Created" },
];

interface DocumentListProps {
  documents: SavedDocument[];
  sort: SortState;
  /** Called with the column clicked; the parent owns direction toggling. */
  onSortChange: (key: SortKey) => void;
  onDownload: (doc: SavedDocument) => void;
  onDelete: (doc: SavedDocument) => void;
  /** document_id currently fetching a download URL, if any. */
  downloadingId?: string | null;
}

export const DocumentList: React.FC<DocumentListProps> = ({
  documents,
  sort,
  onSortChange,
  onDownload,
  onDelete,
  downloadingId,
}) => {
  return (
    <div className="document-list" role="table" aria-label="Saved documents">
      <div className="document-list__head" role="row">
        {COLUMNS.map((column) => {
          const active = sort.key === column.key;
          return (
            <span
              key={column.key}
              role="columnheader"
              // aria-sort is what announces the current order, so the arrow
              // glyph can stay decorative.
              aria-sort={
                active
                  ? sort.direction === "asc"
                    ? "ascending"
                    : "descending"
                  : "none"
              }
              className={`document-list__head-cell ${column.modifier ?? ""}`}
            >
              <button
                type="button"
                className={`document-list__sort${
                  active ? " document-list__sort--active" : ""
                }`}
                onClick={() => onSortChange(column.key)}
              >
                {column.label}
                <svg
                  className={`document-list__sort-icon${
                    active && sort.direction === "asc"
                      ? " document-list__sort-icon--asc"
                      : ""
                  }`}
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  {active ? (
                    <polyline points="6 9 12 15 18 9" />
                  ) : (
                    // Unsorted columns get a neutral up/down glyph — a lone
                    // down-chevron would read as "already sorted descending".
                    <>
                      <polyline points="6 15 12 20 18 15" />
                      <polyline points="6 9 12 4 18 9" />
                    </>
                  )}
                </svg>
              </button>
            </span>
          );
        })}
        <span
          className="document-list__head-cell document-list__head-cell--actions"
          role="columnheader"
        >
          <span className="sr-only">Actions</span>
        </span>
      </div>

      {documents.map((doc) => (
        <DocumentRow
          key={doc.document_id}
          document={doc}
          onDownload={onDownload}
          onDelete={onDelete}
          downloading={downloadingId === doc.document_id}
        />
      ))}
    </div>
  );
};
